/**
 * Project List Page
 * Shows all projects with ability to create/manage
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as BackIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import AppBar from '../elements/AppBar';
import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import FilterSection from '../elements/FilterSection';
import ProjectDetailsForm, { isLocationSatisfied, isNameSatisfied, resolveLocationForSave, GeocodeNotFoundError, GeocodeAuthError } from '../elements/ProjectDetailsForm';
import useProjectSelector from '../../hooks/useProjectSelector';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { memberOf } from '../../store/slices/authSlice';
import { updateProjectAction, deleteProjectAction, fetchProjectsByCustomer } from '../../store/slices/projectSlice';
import { formatDate } from '../../utils/helpers';
import { colorPalette } from '../../theme';
import StatusChip from '../elements/StatusChip';
import { PROJECT_STATUS_MAP } from '../../utils/statusTone';
import MapAreasDialog from '../elements/MapAreasDialog';

// R-22: MUI Chip's `color` prop takes 'default' | 'primary' | 'success' |
// 'warning' | 'error' | 'info' | 'secondary'. Map the abstract tone names
// from PROJECT_STATUS_MAP into that vocabulary here.
const TONE_TO_CHIP_COLOR = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
  primary: 'primary',
  neutral: 'default',
};

const ProjectListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { projectList, inactiveProjectList } = useAppSelector((state) => state.project);
  const { activeCustomer } = useAppSelector((state) => state.customer);
  const { ProjectSelectorComponent } = useProjectSelector();
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  // AST-125 RB-5: mapped areas are edited from here now (the Map Areas
  // button on the project map is gone). Reuses the same MapAreasDialog, so
  // there is one way to map areas, not two that can drift apart.
  const [mapAreasOpen, setMapAreasOpen] = useState(false);
  const [areasSaved, setAreasSaved] = useState(null); // confirmation line
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    client: '',
    status: 'active',
    type: 'project',
    startDate: '',
    endDate: '',
    // ASS-80: flat form-layer keys, same shape ProjectDetailsForm reads/
    // writes and the wizard already used -- collapsed into the structured
    // `location` wire object on save, never sent flat.
    locationAddress: '',
    locationLat: '',
    locationLng: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Create project dialog (Iteration 12)

  useEffect(() => {
    const checkAdmin = async () => {
      const result = await dispatch(memberOf(/admins|customerAdmin[\S]+/));
      setIsAdmin(result);
    };
    checkAdmin();
  }, [dispatch]);

  // Handle opening edit modal
  const handleOpenEditModal = (project) => {
    setEditingProject(project);
    setAreasSaved(null);
    setEditFormData({
      name: project.name || '',
      description: project.description || '',
      client: project.client || '',
      status: project.status || 'active',
      type: project.type || 'project',
      startDate: project.startDate || '',
      endDate: project.endDate || '',
      // ASS-80: prefill from project.location -- the same structured shape
      // the wizard writes on create (ProjectSetupWizard.jsx ~:209-214).
      // ASS-82 fix: also prefill locationSource so a previously-geocoded
      // project reopens showing the read-only coordinates line instead of
      // editable fields, per the ticket's acceptance test.
      locationAddress: project.location?.address || '',
      locationLat: project.location?.lat != null ? String(project.location.lat) : '',
      locationLng: project.location?.lng != null ? String(project.location.lng) : '',
      locationSource: project.location?.source,
      locationPlaceName: project.location?.placeName,
    });
    setEditError(null);
    setEditModalOpen(true);
  };

  // Handle edit form submission
  const handleSaveProject = async () => {
    if (!isNameSatisfied(editFormData)) {
      setEditError('Project name is required');
      return;
    }
    // ASS-80 fix: location is only required for Manual projects -- Beacon
    // and Both projects satisfy R-22 via their KMZ boundary and don't need
    // an address/coordinates too. Without this gate, a beacon project with
    // a boundary but no address (e.g. Duluth Loop) couldn't be saved.
    const locationRequired = editingProject?.trackingMode === 'manual';
    if (locationRequired && !isLocationSatisfied(editFormData)) {
      setEditError('Location is required — enter an address, or a latitude and longitude, or both.');
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      // ASS-82: resolve address-only locations via Mapbox geocoding before
      // building the payload. Entered coordinates win and skip the network
      // call; a geocode failure blocks Save here with the exact copy the
      // brief specifies; a 401 (bad token) is a distinct, louder error.
      let resolved;
      try {
        resolved = await resolveLocationForSave(editFormData);
      } catch (geocodeErr) {
        if (geocodeErr instanceof GeocodeAuthError) {
          console.error('Mapbox token rejected (401):', geocodeErr.message);
          setEditError('Address lookup is unavailable — add a latitude and longitude.');
        } else if (geocodeErr instanceof GeocodeNotFoundError) {
          setEditError("We couldn't find that address — add a latitude and longitude.");
        } else {
          setEditError(geocodeErr.message || 'Could not resolve the location.');
        }
        setEditLoading(false);
        return;
      }

      if (resolved.source === 'geocoded') {
        setEditFormData(prev => ({
          ...prev,
          locationLat: String(resolved.lat),
          locationLng: String(resolved.lng),
          locationSource: 'geocoded',
          locationPlaceName: resolved.placeName,
        }));
      }

      // ASS-80: write the same structured wire shape the wizard writes
      // (location: { address, lat, lng }), never the flat locationXxx keys.
      // Latitude/longitude are stored as numbers, not strings. ASS-82 fix:
      // also persist source, so reopening a geocoded project can show the
      // read-only coordinates line without re-deriving how they got there.
      // ASS-82 follow-up: also persist placeName, shown back to the user on
      // reopen so a wrong-but-plausible match can be caught.
      const location = { address: resolved.address, lat: resolved.lat, lng: resolved.lng, source: resolved.source, placeName: resolved.placeName };

      // eslint-disable-next-line no-unused-vars
      const { locationAddress: _a, locationLat: _lat, locationLng: _lng, locationSource: _src, locationPlaceName: _pn, ...rest } = editFormData;
      const payload = { ...rest, location };

      await dispatch(updateProjectAction({ id: editingProject.id, data: payload })).unwrap();
      setEditModalOpen(false);
      setEditingProject(null);
      if (activeCustomer?.id) {
        dispatch(fetchProjectsByCustomer(activeCustomer.id));
      }
    } catch (err) {
      setEditError(err.message || 'Failed to update project');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle opening delete modal
  const handleOpenDeleteModal = (project) => {
    setDeletingProject(project);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteProject = async () => {
    setDeleteLoading(true);

    try {
      await dispatch(deleteProjectAction(deletingProject.id)).unwrap();
      setDeleteModalOpen(false);
      setDeletingProject(null);
      if (activeCustomer?.id) {
        dispatch(fetchProjectsByCustomer(activeCustomer.id));
      }
    } catch (err) {
      console.error('Failed to delete project:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter projects by status. ASS-84: show active AND draft -- draft
  // projects (R-22: saved with no boundary/location) were being filtered
  // out entirely here, so the Draft chip built for this table could never
  // render. Inactive stays hidden, exactly as before.
  const filteredProjects = projectList
    .filter((project) => project.status === 'active' || project.status === 'draft')
    .filter((project) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        project.name?.toLowerCase().includes(query) ||
        project.client?.toLowerCase().includes(query) ||
        project.description?.toLowerCase().includes(query)
      );
    });

  // R-22: route the project-status Chip through PROJECT_STATUS_MAP so Draft
  // renders with its central label + tone instead of hand-rolled values that
  // drift from statusTone.js. Falls back to 'default' for legacy statuses that
  // are not in the map (activating / pending / complete / etc.).
  const getStatusColor = (status) => {
    const entry = PROJECT_STATUS_MAP[status];
    if (!entry) return 'default';
    // Map the tone name to a MUI Chip color prop.
    return TONE_TO_CHIP_COLOR[entry.tone] || 'default';
  };

  const getStatusLabel = (status) => {
    const entry = PROJECT_STATUS_MAP[status];
    return entry ? entry.label : (status || '-');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Home Button (ASS-79: relabelled from "View Map" -- it already
            navigated to "/", which is Home, not the map view; label-only
            change, per Mark's Slack 09.10.26 09:12). */}
        <Button
          variant="text"
          color="secondary"
          startIcon={<BackIcon />}
          onClick={() => navigate('/')}
          sx={{ height: 44, mb: 2 }}
        >
          Home
        </Button>

        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            Projects
          </PageTitle>
          {isAdmin && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/projects/new')}
                sx={{ height: 44 }}
              >
                New Project
              </Button>
            </Box>
          )}
        </Box>

        {/* Search */}
        <FilterSection>
          <TextField
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </FilterSection>

        {/* Empty State */}
        {projectList.length === 0 && inactiveProjectList.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No projects found
            </Typography>
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/projects/new')}
                sx={{ mt: 2 }}
              >
                Create Your First Project
              </Button>
            )}
          </Box>
        )}

        {/* Projects Table */}
        {filteredProjects.length > 0 && (
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #4A4847' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Tracking</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell align="right" sx={{ width: 140 }} aria-hidden="true" />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProjects.map((project) => (
                  <TableRow
                    key={project.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      height: 56,
                      // R-24: reveal row actions on hover/focus at pointer widths;
                      // touch devices (no hover) always see them.
                      '@media (hover: hover)': {
                        '& .row-actions': { opacity: 0, transition: 'opacity 120ms ease-in-out' },
                        '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
                      },
                    }}
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <TableCell>
                      <Typography fontWeight={500}>
                        {project.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        status={project.trackingMode || 'beacon'}
                        domain="capability"
                      />
                    </TableCell>
                    <TableCell>{project.client || '-'}</TableCell>
                    <TableCell>{project.type || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(project.status)}
                        size="small"
                        color={getStatusColor(project.status)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(project.startDate) || '-'}</TableCell>
                    <TableCell align="right" className="row-actions" sx={{ width: 140 }}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          aria-label="View project"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projects/${project.id}`);
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              aria-label="Edit project"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditModal(project);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              aria-label="Delete project"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteModal(project);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageBody>

      {/* Edit Project Modal. ASS-80: renders the shared ProjectDetailsForm
          -- same fields, same order as the New-project wizard -- instead
          of this modal's own hand-rolled fields. No KMZ. Footer:
          Cancel · Save. */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Project</DialogTitle>
          <DialogContent>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}
            <Box sx={{ mt: 1 }}>
              <ProjectDetailsForm
                value={editFormData}
                onChange={setEditFormData}
                locationRequired={editingProject?.trackingMode === 'manual'}
              />
            </Box>
            {/* AST-125 RB-5 / AC-3: Mapped areas, which used to be the Map
                Areas button on the project map. Same tracking modes as that
                button (manual only -- that's where KMZ areas are mapped).
                Opens the existing dialog, which loads the stored KMZ, lets
                you reassign every folder and placemark, and saves straight
                away -- independent of this form's Save. */}
            {editingProject?.trackingMode === 'manual' && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Mapped areas</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  The structures, yards and other areas on this project&apos;s map, from its KMZ/KML file.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => { setAreasSaved(null); setMapAreasOpen(true); }}
                  disabled={editLoading}
                  sx={{ textTransform: 'none' }}
                >
                  Edit mapped areas
                </Button>
                {areasSaved && (
                  <Typography role="status" variant="body2" color="success.main" sx={{ mt: 1 }}>{areasSaved}</Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditModalOpen(false)} disabled={editLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveProject}
              variant="contained"
              disabled={editLoading || !isNameSatisfied(editFormData) || (editingProject?.trackingMode === 'manual' && !isLocationSatisfied(editFormData))}
              sx={{ backgroundColor: colorPalette.asseTracOrange }}
            >
              {editLoading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      {/* AST-125 RB-5: opened from the Edit Project modal above. Saving here
          is independent of that modal's Save, exactly as the old button. */}
      {editingProject?.trackingMode === 'manual' && (
        <MapAreasDialog
          open={mapAreasOpen}
          projectId={editingProject.id}
          onClose={() => setMapAreasOpen(false)}
          onSaved={(res) => {
            const n = res?.count ?? 0;
            const c = res?.changes || {};
            const parts = [c.created && `${c.created} added`, c.retired && `${c.retired} removed`].filter(Boolean);
            setAreasSaved(`Mapped areas saved: ${n} element${n === 1 ? '' : 's'}${parts.length ? ` (${parts.join(', ')})` : ''}.`);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingProject?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProject}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* R-18a: create-project modal removed. Wizard at /projects/new is the
          single entry point. R-18b adds the tracking-mode step to the wizard. */}
    </Box>
  );
};

export default ProjectListPage;
